import { useState } from 'react';

import { Colors } from 'constants/ui';
import { EmptyState } from 'components/EmptyState';
import { PageLoader } from 'components/PageLoader';
import { PublicPortfolio } from '../api';
import { formatCompactNumber } from 'utils/formatters';

import { TIMEFRAME_DISPLAY } from './constants';
import { BookNowModal } from './ui/book-now';
import { CopyProfileButton } from './ui/copy-profile-button';
import { ProfileHeader } from './ui/header';
import { IntentModal } from './ui/intent-modal';
import { MessageModal } from './ui/message-modal';
import { PitchModal } from './ui/pitch-modal';
import { PortfolioGallery } from './ui/portfolio';
import { ReputationBreakdown } from './ui/reputation';
import { RequestProjectModal } from './ui/request-project-modal';
import { ReviewsList } from './ui/reviews';
import { ServicesList } from './ui/services';
import { TeamGrid } from './ui/team-grid';
import * as styled from './styles';

type Props = {
  data: Kudable.ProfileData | null;
  error: Profile.ProfileError;
  onRetry: () => void;
  reviews: Kudable.Review[];
  hasMore: boolean;
  reviewsLoading: boolean;
  onLoadMore: () => void;
  token: string;
  slotsData: Kudable.SlotsData;
  slotsLoading: boolean;
  bookingOpen: boolean;
  onBookNow: () => void;
  onBookingClose: () => void;
  portfolio: PublicPortfolio | null;
  sort: Profile.ReviewSort;
  badge: Profile.ReviewBadgeFilter;
  search: string;
  onSortChange: (sort: Profile.ReviewSort) => void;
  onBadgeChange: (badge: Profile.ReviewBadgeFilter) => void;
  onSearchChange: (query: string) => void;
};

export const ProfileScene = ({
  data,
  error,
  onRetry,
  reviews,
  hasMore,
  reviewsLoading,
  onLoadMore,
  token,
  slotsData,
  slotsLoading,
  bookingOpen,
  onBookNow,
  onBookingClose,
  portfolio,
  sort,
  badge,
  search,
  onSortChange,
  onBadgeChange,
  onSearchChange,
}: Props) => {
  const [messageOpen, setMessageOpen] = useState(false);
  const [pitchOpen, setPitchOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [intentOpen, setIntentOpen] = useState(false);

  if (error === 'not_found') {
    return (
      <EmptyState
        fullPage
        icon="🔍"
        title="Profile not found"
        body="This profile doesn't exist or has been removed. Double-check the link you followed."
      />
    );
  }

  if (error === 'unreachable') {
    return (
      <EmptyState
        fullPage
        icon="📡"
        title="Can't reach Kudable right now"
        body="We couldn't connect to the server. Check your internet connection, then try again."
        action={{ label: 'Try again', onClick: onRetry }}
      />
    );
  }

  if (!data) return <PageLoader fullPage />;

  if (!data.subscription_active) {
    return (
      <EmptyState
        fullPage
        icon="🔒"
        title="Account not enabled"
        body="This profile is not currently active. Please check back later."
      />
    );
  }

  const { provider, reputation, services, features } = data;
  const bookingsEnabled = features?.bookings_enabled === true;
  const providerName = `${provider.first_name} ${provider.last_name}`.trim();
  const accentColor = provider.accent_color ?? Colors.accent;

  const isCreatorMode = provider.provider_mode === 'creator';

  const intents: Profile.IntentKey[] = [];
  if (provider.accepts_bookings && bookingsEnabled) intents.push('book');
  if (provider.accepts_projects) intents.push('project');
  if (isCreatorMode && provider.open_to_collabs) intents.push('collab');
  if (!isCreatorMode) intents.push('message');

  const handlePickIntent = (key: Profile.IntentKey) => {
    setIntentOpen(false);
    if (key === 'book') onBookNow();
    else if (key === 'project') setProjectOpen(true);
    else if (key === 'collab') setPitchOpen(true);
    else if (key === 'message') setMessageOpen(true);
  };

  return (
    <div>
      <styled.TopBar>
        <styled.TopBarInner>
          <styled.Logo href="/">
            <styled.LogoMark>K</styled.LogoMark>
            <styled.LogoWordmark>kudable</styled.LogoWordmark>
          </styled.Logo>
          <styled.PromoCta href="/">✨ Get your own profile</styled.PromoCta>
        </styled.TopBarInner>
      </styled.TopBar>
      <styled.Container>
        <styled.Card>
          <ProfileHeader
            provider={provider}
            profileToken={token}
            reputation={reputation}
            responseTimeHours={data.response_time_hours}
            intents={intents}
            subscriptionActive={data.subscription_active}
            onPickIntent={handlePickIntent}
            onOpenChooser={() => setIntentOpen(true)}
          />
          <CopyProfileButton
            provider={provider}
            services={services}
            token={token}
          />
        </styled.Card>

        {provider.company_mode && provider.team_members?.length > 0 && (
          <styled.Card>
            <TeamGrid
              members={provider.team_members}
              accentColor={accentColor}
            />
          </styled.Card>
        )}

        {bookingsEnabled && services?.length > 0 && (
          <styled.Card>
            <styled.SectionHeader>
              <styled.SectionTitle>Services</styled.SectionTitle>
            </styled.SectionHeader>
            <ServicesList
              services={services}
              currency={data.provider.currency ?? null}
              accentColor={accentColor}
            />
          </styled.Card>
        )}

        {portfolio &&
        ((portfolio.items && portfolio.items.length > 0) ||
          Object.values(portfolio.items_by_section).some(
            (sectionItems) => sectionItems.length > 0,
          )) ? (
          <styled.Card>
            <styled.SectionTitle>Portfolio</styled.SectionTitle>
            <PortfolioGallery portfolio={portfolio} accentColor={accentColor} />
          </styled.Card>
        ) : null}

        {reputation.category_averages?.length > 0 && (
          <styled.Card>
            <styled.SectionTitle>Reputation Breakdown</styled.SectionTitle>
            <ReputationBreakdown
              categoryAverages={reputation.category_averages}
            />
          </styled.Card>
        )}

        {provider.provider_mode === 'creator' &&
          reviews.filter((review) => review.achievement).length > 0 && (
            <styled.Card>
              <styled.SectionTitle>Selected results</styled.SectionTitle>
              <styled.SelectedResultsRow>
                {reviews
                  .filter((review) => review.achievement)
                  .slice(0, 3)
                  .map((review) => (
                    <styled.ResultCard key={review.id}>
                      <styled.ResultAchievement>
                        🎯 {review.achievement}
                      </styled.ResultAchievement>
                      {review.timeframe ? (
                        <styled.ResultTimeframe>
                          {TIMEFRAME_DISPLAY[review.timeframe] ?? ''}
                        </styled.ResultTimeframe>
                      ) : null}
                      <styled.ResultAuthor>
                        — {review.reviewer_name || 'Anonymous'}
                        {review.reviewer_organisation
                          ? `, ${review.reviewer_organisation}`
                          : ''}
                      </styled.ResultAuthor>
                    </styled.ResultCard>
                  ))}
              </styled.SelectedResultsRow>
            </styled.Card>
          )}

        <styled.Card>
          <styled.SectionTitle>
            Reviews ({formatCompactNumber(reputation.review_count)})
          </styled.SectionTitle>
          <ReviewsList
            reviews={reviews}
            hasMore={hasMore}
            isLoading={reviewsLoading}
            onLoadMore={onLoadMore}
            sort={sort}
            badge={badge}
            search={search}
            accentColor={accentColor}
            onSortChange={onSortChange}
            onBadgeChange={onBadgeChange}
            onSearchChange={onSearchChange}
          />
        </styled.Card>
      </styled.Container>

      {bookingOpen && (
        <BookNowModal
          token={token}
          services={services}
          currency={provider.currency ?? null}
          slotsData={slotsData}
          slotsLoading={slotsLoading}
          accentColor={accentColor}
          onClose={onBookingClose}
        />
      )}

      {messageOpen && (
        <MessageModal
          token={token}
          services={services}
          providerName={providerName}
          acceptsBookings={provider.accepts_bookings}
          responseTimeHours={data.response_time_hours}
          accentColor={accentColor}
          onClose={() => setMessageOpen(false)}
        />
      )}

      {pitchOpen && (
        <PitchModal
          token={token}
          providerName={providerName}
          responseTimeHours={data.response_time_hours}
          questions={provider.pitch_questions ?? []}
          accentColor={accentColor}
          onClose={() => setPitchOpen(false)}
        />
      )}

      {projectOpen && (
        <RequestProjectModal
          token={token}
          providerName={providerName}
          accentColor={accentColor}
          services={services ?? []}
          onClose={() => setProjectOpen(false)}
        />
      )}

      {intentOpen && (
        <IntentModal
          providerName={provider.first_name || providerName}
          accentColor={accentColor}
          available={intents}
          onPick={handlePickIntent}
          onClose={() => setIntentOpen(false)}
        />
      )}
    </div>
  );
};
